import './instrument';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ThemeProvider } from '@mui/material/styles';
import { router } from './router';
import { adminGlobalStyles, adminTheme } from './theme';
import './styles.css';

const queryClient = new QueryClient();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found');

// Dev-only design-system gallery (CAE-1537), mirroring caesars-over-under's
// convention: a browsable page at #/gallery, mounted OUTSIDE the provider tree
// because the token surface is pure presentational. In a production build
// import.meta.env.DEV is false, so this branch — and with it the dynamic
// import — is dead-code-eliminated and the gallery never enters the bundle.
//
// The hash is read once at module load, so adding #/gallery to an already-open
// tab needs a reload to take effect — hence the hashchange listener below.
const isGalleryHash = () => window.location.hash.startsWith('#/gallery');

if (import.meta.env.DEV) {
  // Crossing the #/gallery boundary in EITHER direction swaps which tree is
  // mounted, so reload rather than trying to tear down the router in place.
  // Registered before either branch mounts: when the gallery is the mounted
  // tree, this is the only thing that can get you back to the app.
  const mountedGallery = isGalleryHash();
  window.addEventListener('hashchange', () => {
    if (isGalleryHash() !== mountedGallery) window.location.reload();
  });
}

if (import.meta.env.DEV && isGalleryHash()) {
  void import('./gallery/gallery')
    .then(({ Gallery }) => {
      createRoot(rootElement).render(
        <StrictMode>
          <Gallery />
        </StrictMode>,
      );
    })
    .catch((error: unknown) => {
      // Without this the page stays blank on a compile error in the gallery —
      // the one situation this dev-only branch ever runs in.
      console.error('[gallery] failed to load', error);
    });
} else {
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={adminTheme}>
          <CssBaseline />
          <GlobalStyles styles={adminGlobalStyles} />
          <RouterProvider router={router} />
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
